
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcessClientPayment } from '@/hooks/clients/useProcessClientPayment';
import { usePaymentForm } from '@/hooks/clients/usePaymentForm';
import { toast } from 'sonner';
import PaymentSubscriptionSelector from './PaymentSubscriptionSelector';
import PaymentFormFields from './PaymentFormFields';
import PaymentFormActions from './PaymentFormActions';
import { ClientSubscription } from '@/models/Client';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';

interface PaymentDialogContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  client: any;
  subscription?: ClientSubscription;
}

const PaymentDialogContent: React.FC<PaymentDialogContentProps> = ({
  open,
  onOpenChange,
  clientId,
  clientName,
  client,
  subscription,
}) => {
  const { t } = useTranslation();
  const { mutate: processPayment, isPending } = useProcessClientPayment();
  const { workoutTypes = [] } = useWorkoutTypes();

  const {
    paymentAmount,
    setPaymentAmount,
    paymentDate,
    setPaymentDate,
    notes,
    setNotes,
    selectedSubscriptionId,
    setSelectedSubscriptionId,
  } = usePaymentForm({ 
    open, 
    subscription,
    activeSubscriptions: client.subscriptions.filter(sub => sub.isActive),
    clientId
  });

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || t('clients.unknownWorkoutType');
  };

  // Debug logging
  useEffect(() => {
    if (open && clientId) {
      console.log('PaymentDialog opened for client:', clientId);
      console.log('Pre-selected subscription:', subscription);
      console.log('Selected subscription ID:', selectedSubscriptionId);
    }
  }, [open, clientId, subscription, selectedSubscriptionId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('clients.invalidPaymentAmount'));
      return;
    }

    if (!selectedSubscriptionId) {
      toast.error('Παρακαλώ επιλέξτε μία συνδρομή για πληρωμή');
      return;
    }

    console.log('Processing subscription payment:', {
      subscriptionId: selectedSubscriptionId,
      amount: amount
    });

    processPayment({
      clientId,
      paymentAmount: amount,
      paymentDate,
      notes: notes.trim() || undefined,
      subscriptionId: selectedSubscriptionId
    }, {
      onSuccess: (result) => {
        console.log('Payment processed successfully:', result);
        
        let statusMessage = '';
        const paymentStatus = result.subscription_payment_status || result.client_payment_status;
        if (paymentStatus === 'paid') {
          statusMessage = t('clients.paid');
        } else if (paymentStatus === 'partial') {
          statusMessage = t('clients.partial');
        } else if (paymentStatus === 'unpaid') {
          statusMessage = t('clients.unpaid');
        } else {
          statusMessage = t('clients.pending');
        }
        
        const paymentMessage = `€${result.payment_amount.toFixed(2)}`;
        const selectedSub = client.subscriptions.find(sub => sub.id === selectedSubscriptionId);
        const subscriptionContext = selectedSub ? ` - ${getWorkoutTypeName(selectedSub.workoutTypeId)}` : '';
        
        toast.success(
          `${t('clients.paymentProcessed')}${subscriptionContext} - ${t('clients.paymentStatus')}: ${statusMessage} (${paymentMessage})`
        );
        
        onOpenChange(false);
      },
      onError: (error) => {
        console.error('Payment processing failed:', error);
        toast.error(`${t('clients.paymentFailed')}: ${error.message}`);
      }
    });
  };

  const selectedSubscription = client.subscriptions.find(sub => sub.id === selectedSubscriptionId);
  const totalMonthlyCost = selectedSubscription ? selectedSubscription.price : 0; // Changed to use price directly

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Subscription selector */}
      <PaymentSubscriptionSelector
        subscriptions={client.subscriptions.filter(sub => sub.isActive)}
        selectedSubscriptionId={selectedSubscriptionId}
        onSubscriptionSelect={setSelectedSubscriptionId}
      />

      {/* Payment form fields */}
      <PaymentFormFields
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        paymentDate={paymentDate}
        setPaymentDate={setPaymentDate}
        notes={notes}
        setNotes={setNotes}
        totalMonthlyCost={totalMonthlyCost}
      />

      {/* Action buttons */}
      <PaymentFormActions
        onCancel={() => onOpenChange(false)}
        isPending={isPending}
        paymentAmount={paymentAmount}
        disabled={!selectedSubscriptionId}
      />
    </form>
  );
};

export default PaymentDialogContent;
